import {SierraCanvas} from '@teemill/common/classes';
import {RgbColor, HsvColor} from '@teemill/utilities';

export type OmniCanvasFilters = {
  recolour:
    | {
        rgb: RgbColor;
        mode: string;
        strength: number | undefined;
      }
    | undefined;
  modifyHsv:
    | {
        hsv: HsvColor;
        mode: string;
        strength: number | undefined;
      }
    | undefined;
  stripWhite: number | undefined;
  desaturate: number | undefined;
  opacity: number | undefined;
  subtractBrightness: number | undefined;
  relativeOpacity: number | undefined;
};

export default {
  filterImg(
    img: HTMLImageElement,
    filters: OmniCanvasFilters,
    canvas: SierraCanvas
  ) {
    return new Promise(resolve => {
      let sierraCanvas;

      const {targetWidth, targetHeight} = this.getTargetDimensions(img);

      if (
        !targetWidth ||
        !targetHeight ||
        isNaN(targetWidth) ||
        isNaN(targetHeight)
      ) {
        resolve(img);
        return;
      }

      if (canvas && canvas instanceof SierraCanvas) {
        sierraCanvas = canvas;
      } else {
        sierraCanvas = new SierraCanvas();
      }

      sierraCanvas
        .resizeCanvas(targetWidth, targetHeight)
        .drawImage(img, 0, 0, targetWidth, targetHeight);

      this.applyFilters(filters, sierraCanvas);

      resolve(sierraCanvas);
    });
  },

  getTargetDimensions(img: HTMLImageElement) {
    const aspectRatio = img.width / img.height;

    let targetWidth = img.width;
    let targetHeight = img.height;

    /**
     * Here we scale the image down so that it never exceeds 4096x4096 pixels
     * This is to prevent crashing due to exceeding memory limits
     *
     * 4096x4096 = 512MB
     *
     * 4096*4096 = 16,777,216 pixels
     * Each pixel has 4 components (r, g, b and a), and each component is an
     * 8-bit integer (0-255)
     *
     * 16777216 * 4 * 8 = 536,870,912 bits (ctx.getImageData has no compression)
     *
     * (536870912 / 1024) / 1024 = 512
     */
    if (aspectRatio >= 1) {
      // image is wider than it is tall
      targetWidth = Math.min(targetWidth, 4096);
      targetHeight = (targetWidth / img.width) * img.height;
    } else {
      // image is taller than it is wide
      targetHeight = Math.min(targetHeight, 4096);
      targetWidth = (targetHeight / img.height) * img.width;
    }

    return {
      targetWidth,
      targetHeight,
    };
  },

  applyFilters(filters: OmniCanvasFilters, target: SierraCanvas) {
    if (filters.recolour) {
      target.recolour(
        filters.recolour.rgb,
        filters.recolour.mode,
        filters.recolour.strength
      );
    }

    if (filters.modifyHsv) {
      target.modifyHsv(
        filters.modifyHsv.hsv,
        filters.modifyHsv.mode,
        filters.modifyHsv.strength
      );
    }

    if (filters.stripWhite) {
      target.stripWhite(filters.stripWhite);
    }

    if (filters.desaturate) {
      target.desaturate(filters.desaturate);
    }

    if (filters.opacity) {
      target.opacity(filters.opacity);
    }
  },
};
